import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, CircularProgress } from '@mui/material';
import { FaBullhorn } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { sendPushNotificationToAll } from '../../controllers/pushNotificationController';
import SendIcon from '@mui/icons-material/Send';

const BildirimYayinla = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const validateForm = () => {
        if (!title.trim() || !body.trim()) {
            setError('Lütfen tüm alanları doldurunuz.');
            return false;
        }
        if (title.length > 200) {
            setError('Başlık en fazla 200 karakter olabilir.');
            return false;
        }
        if (body.length > 1000) {
            setError('İçerik en fazla 1000 karakter olabilir.');
            return false;
        }
        return true;
    };

    const handleSend = async () => {
        if (!validateForm()) return;

        setSending(true);
        setError('');
        setSuccess('');

        try {
            const payload = {
                title: title.trim(),
                body: body.trim(),
                data: null,
                source: null,
                sourceId: null,
            };

            const result = await sendPushNotificationToAll(payload);
            const successMessage = `Bildirim tüm üyelere gönderildi. Toplam: ${result.totalRecipients || 0}, Başarılı: ${result.successCount || 0}, Başarısız: ${result.failureCount || 0}`;

            setSuccess(successMessage);
            setTitle('');
            setBody('');
        } catch (err) {
            setError(err?.message || 'Bildirim gönderilemedi.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Card sx={{ flex: '0 0 calc(33.333% - 16px)', minWidth: 300, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FaBullhorn style={{ fontSize: '18px', color: '#0E4D94' }} />
                        </Box>
                        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>
                            Bildirim Yayınla
                        </Typography>
                    </Stack>
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate('/notifications/app-notifications')}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: 500,
                            backgroundColor: 'transparent',
                            color: '#0E4D94',
                            textDecoration: 'underline',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Tüm Bildirimler
                    </Button>
                </Stack>

                <Stack spacing={2}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Başlık
                        </Typography>
                        <TextField
                            value={title}
                            onChange={(e) => {
                                setTitle(e.target.value);
                                setError('');
                            }}
                            fullWidth
                            size="small"
                            placeholder=""
                            inputProps={{ maxLength: 64 }}
                            helperText={`${title.length}/64 Karakter`}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                                '& .MuiFormHelperText-root': {
                                    fontSize: '12px',
                                    mt: 0.5,
                                    ml: 0,
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            İçerik
                        </Typography>
                        <TextField
                            value={body}
                            onChange={(e) => {
                                setBody(e.target.value);
                                setError('');
                            }}
                            fullWidth
                            multiline
                            rows={1.5}
                            placeholder=""
                            inputProps={{ maxLength: 178 }}
                            helperText={`${body.length}/178 Karakter`}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                                '& .MuiFormHelperText-root': {
                                    fontSize: '12px',
                                    mt: 0.5,
                                    ml: 0,
                                },
                            }}
                        />
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ fontSize: '13px' }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ fontSize: '13px' }}>
                            {success}
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 14 }} />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#0E4D94',
                            borderRadius: '5px',
                            fontSize: '14px',
                            fontWeight: 500,
                            py: 1,
                            '&:hover': {
                                backgroundColor: '#0d3d7a',
                            },
                        }}
                    >
                        {sending ? 'Gönderiliyor...' : 'Bildirim Gönder'}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default BildirimYayinla;
